import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, catchError, map } from 'rxjs';
import { ContributionReasonCode, PensionContributionEmployee, PensionSubmissionError } from './pension-contribution-employee.model';
import { PensionContributionApiResponse } from './pension-contribution.dto';
import { PensionEnrollmentRecord } from './pension-enrollment-record.model';
import { environment } from '@env/environment';
import { LoggingService } from '@logging/logging.service';
import { ClosedPeriodOperationType } from '../../models/closed-period-operation-type.enum';
import { PensionSubmissionStatus, PensionSubmissionStatusLabels } from '../../models/pension-submission-status.enum';

@Injectable()
export class PensionContributionsDataProvider {
  private readonly apiRoot = `${environment.apiPaths.pensions}`;

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggingService
  ) {}

  getSubmissionContributions$(closedPeriodId: number): Observable<PensionContributionEmployee[]> {
    return this.http.get<PensionContributionApiResponse[]>(`${this.apiRoot}/submission/${closedPeriodId}/contributions`).pipe(
      map((contributions) => contributions.map((contribution) => this.transformContribution(contribution))),
      catchError((error) => {
        this.logger.error('Failed to fetch pension contributions', error);
        return of([]);
      })
    );
  }

  private transformContribution(apiResponse: PensionContributionApiResponse): PensionContributionEmployee {
    const raw = apiResponse as unknown as Record<string, unknown>;
    const employerRef = (apiResponse.employerRef ?? raw['EmployerRef']) as string | null | undefined;
    const paymentSource = (apiResponse.paymentSource ?? raw['PaymentSource']) as string | null | undefined;
    const hasFromApi = employerRef != null && paymentSource != null && employerRef !== '' && paymentSource !== '';
    const employerRefPaymentSource = hasFromApi ? `${employerRef} / ${paymentSource}` : (apiResponse.pensionGroup ?? '');
    const statusValue = (apiResponse.submissionStatus ?? raw['SubmissionStatus']) as string | null | undefined;
    const submissionStatus = statusValue ? (PensionSubmissionStatusLabels[statusValue as PensionSubmissionStatus] ?? statusValue) : '';
    const contributionReasonDescription =
      apiResponse.contributionReasonDescription ?? (raw['ContributionReasonDescription'] as string | null | undefined) ?? null;
    const contributionReductionReason =
      apiResponse.contributionReductionReason ?? (raw['ContributionReductionReason'] as number | null | undefined) ?? null;
    const requiresReasonCode = apiResponse.hasErrors === true;
    return {
      employeeId: apiResponse.employeeId,
      employmentId: apiResponse.employmentId,
      firstName: apiResponse.firstName ?? '',
      surname: apiResponse.surname ?? '',
      errors: apiResponse.errors,
      periodEarnings: apiResponse.periodEarnings,
      employeeContribution: apiResponse.employeeContribution,
      employerContribution: apiResponse.employerContribution,
      total: apiResponse.total,
      pensionGroup: apiResponse.pensionGroup,
      employerRefPaymentSource,
      hasEmployerRefPaymentSourceFromApi: hasFromApi || undefined,
      submissionStatus,
      contributionReasonDescription,
      contributionReductionReason,
      requiresReasonCode
    };
  }

  getClosedPeriodErrors$(closedPeriodId: number, operationTypes: ClosedPeriodOperationType[]): Observable<PensionSubmissionError[]> {
    let params = new HttpParams();
    operationTypes.forEach((type) => {
      params = params.append('operationTypes', type.toString());
    });

    return this.http.get<PensionSubmissionError[]>(`${this.apiRoot}/submission/${closedPeriodId}/closed-period-errors`, { params }).pipe(
      catchError((error) => {
        this.logger.error('Failed to fetch closed period errors', error);
        return of([]);
      })
    );
  }

  getSubmissionEnrollments$(closedPeriodId: number): Observable<PensionEnrollmentRecord[]> {
    return this.http.get<PensionEnrollmentRecord[]>(`${this.apiRoot}/submission/${closedPeriodId}/enrollments`).pipe(
      catchError((error) => {
        this.logger.error('Failed to fetch enrollment data', error);
        return of([]);
      })
    );
  }

  getContributionReasonCodes$(): Observable<ContributionReasonCode[]> {
    return this.http.get<ContributionReasonCode[]>(`${this.apiRoot}/submission/reason-codes`).pipe(
      catchError((error) => {
        this.logger.error('Failed to fetch contribution reason codes', error);
        return of([]);
      })
    );
  }
}
